import React from "react";
import { Formik } from "formik";
import { Stack, MessageBar, MessageBarType } from "@fluentui/react";
import basicInfoSchema from "./basicInfoValidationSchema";
import BasicInfoFields, { ActionTypes } from "./BasicInfoFields";
import { enableMinimumScore, enableJournalPrompt } from "./ProprietarySpecificFields";


const mapValuesForSubmit = ({
  actionItemType,
  proprietarySpecificContent,
  proprietarySpecificCourse,
  proprietarySpecificJournalPrompt,
  journalPromptBrief,
  proprietaryGeneralActionType,
  proprietaryGeneralTopicID,
  proprietarySpecificActionType,
  proprietaryGeneralAmount,
  thirdPartyActionTitle,
  thirdPartyCompletionMechanism,
  thirdPartyHowItWorks,
  thirdPartyHowToComplete,
  questionActionTitle,
  questionText,
  questionHelpText,
  forceMinimumScore,
  minimumScore,
}) => {
  const specificPointsID = proprietarySpecificActionType;
  const generalPointsID = proprietaryGeneralActionType;

  return {
    actionItemType: actionItemType,
    ...(actionItemType === ActionTypes.ProprietarySpecific && {
      proprietarySpecificContentID: proprietarySpecificContent,
      proprietarySpecificCourseID: proprietarySpecificCourse,
      ...(enableJournalPrompt.includes(Number(specificPointsID)) && {
        proprietarySpecificJournalPromptID: proprietarySpecificJournalPrompt,
        journalPromptBrief: journalPromptBrief,
      }),
      pointsID: specificPointsID,
      ...(enableMinimumScore.includes(Number(specificPointsID)) && {
        minimumScore: forceMinimumScore ? Number(minimumScore) : null,
      }),
    }),
    ...(actionItemType === ActionTypes.ProprietaryGeneral && {
      proprietaryGeneralTopicID: proprietaryGeneralTopicID,
      proprietaryGeneralAmount: proprietaryGeneralAmount,
      pointsID: generalPointsID,
      ...(enableMinimumScore.includes(Number(generalPointsID)) && {
        minimumScore: forceMinimumScore ? Number(minimumScore) : null,
      }),
    }),
    ...(actionItemType === ActionTypes.ThirdParty && {
      thirdPartyActionTitle: thirdPartyActionTitle,
      thirdPartyCompletionMechanism: thirdPartyCompletionMechanism,
      thirdPartyHowItWorks: thirdPartyHowItWorks,
      ...(thirdPartyCompletionMechanism === "Client-reported" && {
        thirdPartyHowToComplete: thirdPartyHowToComplete,
      }),
    }),
    ...(actionItemType === ActionTypes.OpenTextQuestion && {
      questionActionTitle: questionActionTitle,
      questionText: questionText,
      questionHelpText: questionHelpText,
    }),
  };
};

const mapInitialValues = (values) => {
  if (!values) return {};
  return {
    ...values,
    ...(values.actionItemType === ActionTypes.ProprietarySpecific && {
      proprietarySpecificActionType: values.pointsID,
    }),
    ...(values.actionItemType === ActionTypes.ProprietaryGeneral && {
      proprietaryGeneralActionType: values.pointsID,
    }),
    proprietarySpecificContent: values.proprietarySpecificContentID,
    proprietarySpecificCourse: values.proprietarySpecificCourseID,
    proprietarySpecificJournalPrompt: values.proprietarySpecificJournalPromptID,
    journalPromptBrief: values.journalPromptBrief ?? "",
    forceMinimumScore: values.minimumScore != null,
    minimumScore: values.minimumScore ?? 100,
  };
};

const extractErrorMessages = (error) => {
  const data = error?.response?.data;
  if (!data) return [error.message || "An unexpected error occurred."];

  // Handle custom Response format: { errors: [{ errorMessage: "..." }] }
  if (Array.isArray(data.errors)) {
    return data.errors.map((e) => e.errorMessage || e.ErrorMessage || JSON.stringify(e));
  }

  // Handle ASP.NET ValidationProblemDetails: { errors: { "Prop": ["msg"] } }
  if (data.errors && typeof data.errors === "object") {
    return Object.values(data.errors).flat();
  }

  if (data.title) return [data.title];
  if (typeof data === "string") return [data];

  return ["An unexpected error occurred."];
};

const BasicInfoForm = ({ postForm, actionItemData, submitting, isWizard, onCreateSuccess, onUpdateSuccess }) => {
  return (
    <Formik
      initialValues={mapInitialValues(actionItemData)}
      onSubmit={async (values, { setSubmitting, resetForm, setStatus }) => {
        setStatus(undefined);
        try {
          const mappedValues = mapValuesForSubmit(values);
          console.log('=== BasicInfoForm Submit Debug ===');
          console.log('Raw form values:', values);
          console.log('Mapped values for submit:', mappedValues);
          console.log('actionItemData:', actionItemData);
          console.log('Method:', actionItemData ? "put" : "post");
          
          const response = await postForm({
            data: mappedValues,
            method: actionItemData ? "put" : "post",
          });
          console.log('Response:', response);
          
          // If this is a new action item (create), redirect to the edit page
          if (!actionItemData && response?.data?.actionItemID && onCreateSuccess) {
            resetForm();
            onCreateSuccess(response.data.actionItemID);
            return; // Don't update any more state after navigation
          }
          // For updates, call the success callback
          if (onUpdateSuccess) {
            onUpdateSuccess();
            // Reset form dirty state after successful save
            resetForm({ values: values });
          }
        } catch (e) {
          console.error(e);
          setStatus({ errors: extractErrorMessages(e) });
        } finally {
          setSubmitting(false);
        }
      }}
      validationSchema={basicInfoSchema}
    >
      {({ dirty, status, setStatus }) => (
        <Stack tokens={{ childrenGap: 20 }}>
          {status?.errors && (
            <MessageBar
              messageBarType={MessageBarType.error}
              isMultiline
              onDismiss={() => setStatus(undefined)}
            >
              {status.errors.map((msg, i) => (
                <div key={i}>{msg}</div>
              ))}
            </MessageBar>
          )}
          <BasicInfoFields
            isUpdate={!!actionItemData}
            submitting={submitting}
          />
        </Stack>
      )}
    </Formik>
  );
};

export default BasicInfoForm;
